import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { 
  Search, 
  Filter, 
  Plus, 
  Eye,
  Trash2,
  MessageCircle,
  UserPlus,
  Download,
  Loader2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const tiposEvento = [
  "Boda", "XV Años", "Bautizo", "Primera Comunión", "Cumpleaños",
  "Baby Shower", "Despedida de Soltera", "Aniversario", "Graduación", "Otro"
];

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [filterServicio, setFilterServicio] = useState("todos");
  const [deleteId, setDeleteId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "", telefono: "", whatsapp: "", correo: "",
    tipo_evento: "", nombre_festejado: "", fecha_evento: "",
    tipo_servicio: "evento_completo"
  });

  const fetchClientes = useCallback(async () => {
    try {
      const params = {};
      if (filterEstatus !== "todos") params.estatus = filterEstatus;
      if (filterServicio !== "todos") params.tipo_servicio = filterServicio;
      const response = await axios.get(`${API}/clientes`, { params });
      setClientes(response.data);
    } catch {
      toast.error("Error al cargar los clientes");
    } finally {
      setLoading(false);
    }
  }, [filterEstatus, filterServicio]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/clientes/${deleteId}`);
      toast.success("Cliente eliminado");
      await fetchClientes();
    } catch (error) {
      toast.error("Error al eliminar el cliente");
    } finally {
      setDeleteId(null);
    }
  };

  const handleCreateCliente = async () => {
    if (!formData.nombre || !formData.telefono || !formData.whatsapp || !formData.tipo_evento || !formData.nombre_festejado || !formData.fecha_evento) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/clientes`, formData);
      toast.success("Cliente creado correctamente");
      setFormData({ nombre: "", telefono: "", whatsapp: "", correo: "", tipo_evento: "", nombre_festejado: "", fecha_evento: "", tipo_servicio: "evento_completo" });
      await fetchClientes();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear el cliente");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendiente_pago: { label: "Pendiente Pago", className: "badge-pending" },
      comprobante_enviado: { label: "Comprobante Enviado", className: "badge-pending" },
      validado: { label: "Validado", className: "badge-validated" },
      en_diseno: { label: "En Diseño", className: "badge-design" },
      entregado: { label: "Entregado", className: "badge-delivered" },
      seguimiento_evento: { label: "Seguimiento", className: "badge-event" },
      confirmado: { label: "Confirmado", className: "badge-validated" },
      realizado: { label: "Realizado", className: "badge-delivered" },
    };
    return badges[status] || { label: status, className: "badge" };
  };

  const filteredClientes = clientes.filter(cliente =>
    (cliente.nombre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cliente.whatsapp || "").includes(searchTerm) ||
    (cliente.nombre_festejado || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Clientes</h1>
          <p className="text-stone-600 mt-1">{filteredClientes.length} clientes registrados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            toast.info("Generando PDF de clientes...");
            try {
              const res = await axios.get(`${API}/admin/exportar/pdf/clientes`);
              descargarArchivo(res.data.url, res.data.filename);
              toast.success("PDF descargado");
            } catch { toast.error("Error al generar PDF"); }
          }} className="border-stone-300" data-testid="btn-pdf-clientes">
            <Download className="w-4 h-4 mr-2" />
            Descargar PDF
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="btn-nuevo-cliente">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Buscar por nombre, teléfono o festejado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-stone-300"
            data-testid="search-clientes"
          />
        </div>
        <Select value={filterEstatus} onValueChange={setFilterEstatus}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-estatus">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="pendiente_pago">Pendiente Pago</SelectItem>
            <SelectItem value="comprobante_enviado">Comprobante Enviado</SelectItem>
            <SelectItem value="validado">Validado</SelectItem>
            <SelectItem value="en_diseno">En Diseño</SelectItem>
            <SelectItem value="entregado">Entregado</SelectItem>
            <SelectItem value="seguimiento_evento">Seguimiento</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterServicio} onValueChange={setFilterServicio}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-servicio">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            <SelectItem value="evento_completo">Evento completo</SelectItem>
            <SelectItem value="invitacion_cotizacion">Solicita cotización</SelectItem>
            <SelectItem value="solo_invitacion">Prospecto</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredClientes.length > 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-crm">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Evento</th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredClientes.map((cliente) => {
                  const badge = getStatusBadge(cliente.estatus);
                  return (
                    <tr key={cliente.cliente_id} data-testid={`cliente-row-${cliente.cliente_id}`}>
                      <td>
                        <div>
                          <p className="font-medium text-stone-900">{cliente.nombre}</p>
                          <p className="text-xs text-stone-500">{cliente.whatsapp}</p>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-stone-700">{cliente.tipo_evento}</p>
                          <p className="text-xs text-stone-500">{cliente.nombre_festejado}</p>
                        </div>
                      </td>
                      <td className="text-stone-600">{cliente.fecha_evento}</td>
                      <td className="text-stone-600 text-sm">
                        {cliente.tipo_servicio === "evento_completo" ? "Evento" : cliente.tipo_servicio === "invitacion_cotizacion" ? "Cotización" : "Prospecto"}
                      </td>
                      <td>
                        <span className={`badge ${badge.className}`}>{badge.label}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Link to={`/panel/clientes/${cliente.cliente_id}`}>
                            <Button variant="ghost" size="sm" aria-label="Ver cliente" title="Ver detalle" className="text-stone-600 hover:text-rose-900" data-testid={`btn-ver-${cliente.cliente_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <a
                            href={require("../../lib/whatsapp").whatsappUrl(cliente.whatsapp) || "#"}
                            onClick={(e) => {
                              const url = require("../../lib/whatsapp").whatsappUrl(cliente.whatsapp);
                              if (!url) { e.preventDefault(); toast.error("Este cliente no tiene un número de WhatsApp válido"); }
                            }}
                            target="_blank" rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" aria-label="Enviar mensaje por WhatsApp" title="WhatsApp" className="text-stone-600 hover:text-green-600" data-testid={`btn-whatsapp-${cliente.cliente_id}`}>
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button variant="ghost" size="sm" aria-label="Eliminar cliente" title="Eliminar" className="text-stone-600 hover:text-red-600" onClick={() => setDeleteId(cliente.cliente_id)} data-testid={`btn-delete-${cliente.cliente_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">No se encontraron clientes</h3>
          <p className="text-stone-600 mb-4">Agrega tu primer cliente o ajusta los filtros</p>
          <Button onClick={() => setDialogOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      )}

      {/* Nuevo Cliente Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nuevo Cliente</DialogTitle>
            <DialogDescription>Registra los datos del cliente y su evento asociado. Los campos con * son obligatorios.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre completo *</Label>
                <Input value={formData.nombre} onChange={(e) => setFormData(p => ({ ...p, nombre: e.target.value }))} className="border-stone-300" data-testid="input-nombre-cliente" />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input type="email" value={formData.correo} onChange={(e) => setFormData(p => ({ ...p, correo: e.target.value }))} className="border-stone-300" data-testid="input-correo-cliente" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono *</Label>
                <Input value={formData.telefono} onChange={(e) => setFormData(p => ({ ...p, telefono: e.target.value }))} className="border-stone-300" data-testid="input-telefono-cliente" />
              </div>
              <div className="space-y-2">
                <Label>WhatsApp *</Label>
                <Input value={formData.whatsapp} onChange={(e) => setFormData(p => ({ ...p, whatsapp: e.target.value }))} className="border-stone-300" data-testid="input-whatsapp-cliente" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de evento *</Label>
                <Select value={formData.tipo_evento} onValueChange={(v) => setFormData(p => ({ ...p, tipo_evento: v }))}>
                  <SelectTrigger className="border-stone-300" data-testid="select-tipo-evento-cliente">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEvento.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Festejado *</Label>
                <Input value={formData.nombre_festejado} onChange={(e) => setFormData(p => ({ ...p, nombre_festejado: e.target.value }))} className="border-stone-300" data-testid="input-festejado-cliente" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha del evento *</Label>
                <Input type="date" value={formData.fecha_evento} onChange={(e) => setFormData(p => ({ ...p, fecha_evento: e.target.value }))} className="border-stone-300" data-testid="input-fecha-cliente" />
              </div>
              <div className="space-y-2">
                <Label>Tipo de cliente</Label>
                <Select value={formData.tipo_servicio} onValueChange={(v) => setFormData(p => ({ ...p, tipo_servicio: v }))}>
                  <SelectTrigger className="border-stone-300" data-testid="select-servicio-cliente">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo_invitacion">Prospecto</SelectItem>
                    <SelectItem value="invitacion_cotizacion">Cotizacion</SelectItem>
                    <SelectItem value="evento_completo">Evento Completo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleCreateCliente} disabled={saving} className="btn-primary" data-testid="btn-guardar-cliente">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-2" /> Crear Cliente</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los datos asociados a este cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clientes;
